<template>
  <div>
    <h6 class="graph_text mt0 mb2">{{ highestValueText }}</h6>
    <div class="graph_container">
      <div class="flex graph" ref="graph">
        <div class="grid" v-if="yAxisLabels.length > 1">
          <div v-for="label in yAxisLabels" :key="label" class="grid_row">
            <hr :style="`width: ${graphWidth}px`"/>
            <span class="y_axis_text">{{ label }}</span>
          </div>
        </div>

        <div class="flex" ref="bars">
          <div v-for="(data) in statsPerHour" :key="data.key">
            <div class="bar_wrapper">
              <div :style="getChartStyle(data.value)" class="bar"></div>
            </div>
            <div class="axis_text_wrapper">
              <div v-if="displayXAxisLine(data.hour)" class="x_axis_divider"></div>

              <div
                class="x_axis_text"
                :style="getXAxisStyle(data.hour)"
              >{{ getXAxisText(data.hour) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import max from 'lodash/max';

export default {
  name: 'graph',

  props: {
    graphData: { type: Object, required: true },
  },

  data() {
    return { graphWidth: 0 };
  },

  computed: {
    statsPerHour() {
      return new Array(24).fill(null).map((_, index) => this.graphData.statsPerHour[index] || ({
        hour: index,
        value: 0,
        key: index,
      }));
    },

    yAxisLabels() {
      if (this.graphCeiling < 1) {
        return [];
      }
      const steps = [3, 2, 1];
      const totalSteps = 4; // 3,2,1 and this.highestValue
      const stepValue = Math.round(this.graphCeiling / totalSteps * 100) / 100;
      return [
        this.graphCeiling,
        ...steps.map(step => (step === 0 ? 0 : stepValue * step)),
        0,
      ];
    },

    graphCeiling() {
      const ROUND_TO = 20;
      return Math.ceil((this.highestValue || 1) / ROUND_TO) * ROUND_TO;
    },

    highestValue() {
      return max(this.graphData.statsPerHour.map(data => data.value));
    },

    highestValueText() {
      if (!this.highestValue) {
        return this.$t('cms.campaign.NoStatsAvailableLabel');
      }

      const highestValueHours = this.graphData.statsPerHour
        .filter(data => data.value === this.highestValue);

      const highestValueText = `
        ${this.$t('cms.campaign.Highest')}
        ${this.$t(this.graphData.text)}
        ${this.highestValue}${this.graphData.suffix}
      `;

      // if multiple highest hours, dont specify time.
      if (!highestValueHours || highestValueHours.length !== 1) {
        return highestValueText;
      }

      const startHour = highestValueHours[0].hour;
      const endHour = startHour === 23 ? 0 : startHour + 1;
      return `${this.formatHour(startHour)} - ${this.formatHour(endHour)}: ${highestValueText}`;
    },
  },

  mounted() {
    const graphWidthInterval = setInterval(() => {
      try {
        const padding = 6;
        this.graphWidth = this.$refs.bars.clientWidth + padding;
      } catch {
        clearInterval(graphWidthInterval);
      }
    }, 50);

    const bars = this.graphData.statsPerHour;
    const scrollInterval = setInterval(() => {
      if (bars > 0) {
        this.$refs.graph.scrollLeft = (bars * 30) - 50;
        clearInterval(scrollInterval);
      }
    }, 50);
  },

  methods: {
    getChartStyle(value) {
      const emptyHeight = 5;
      const height = value / this.graphCeiling * 100;
      const isMax = height === 100;
      const color = isMax ? this.graphData.isMaxColor : this.graphData.color;
      return `
      width: 50px;
      height: ${height || emptyHeight}%;
      background-color: ${color};`;
    },

    displayXAxisLine(hour) {
      return hour % 3 === 0;
    },

    formatHour(hour) {
      return `${hour + 1 > 9 ? '' : '0'}${hour + 1}:00`;
    },

    getXAxisText(hour) {
      if (hour % 3 === 1) { return ''; }
      return this.formatHour(hour);
    },

    getXAxisStyle(hour) {
      if (hour % 3 === 0) { return 'margin-left: 10px; margin-right: -10px;'; }
      return 'margin-left: -10px; margin-right: 10px;';
    },
  },
};
</script>

<style lang="scss">
.graph_text {
  text-align: right;
  color: $color_gray_text;
  font-style: italic;
}

//to remove the scroll bars
.graph_container {
  height: 126px;
  overflow-y: hidden;
  z-index: 9;
}

.graph {
  overflow-x: scroll;
  overflow-y: hidden;
  padding-top: 10px; // to not cutoff y axis text
  padding-bottom: 10px; //to remove the scroll bars
}

.bar_wrapper {
  padding-right: 6px;
  padding-left: 6px;
  height: $height_graph_bar_max;
  width: $width_graph_bar;
  display: flex;
  overflow: hidden;
}

.bar {
  margin-top: auto;
  margin-bottom: 0;
  border-bottom: unset;
  border-radius: 8px 8px 0 0;
}

.axis_text_wrapper {
  overflow: visible;
  text-overflow: unset;
  text-align: center;
}

.x_axis_text {
  color: $color_gray_text;
  padding-top: 3px;
}

.x_axis_divider {
  font-size: 6px;
  display: block;
  color: $color_gray_text;
  border: 0;
  border-left: 1px solid $color_even_lighter_gray;
  border-style: dashed;
  margin-top: -96px;
  height: 104px;
  margin-bottom: -8px;
}

.grid {
  margin-right: 30px;
  overflow: visible;
  height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.y_axis_text {
  color: $color_gray_text;
  display: block;
  margin-top: -12px;
  padding-right: 12px;
  text-align: center;
  position: absolute;
  background-color: $color_background_slightly_yellow;
  width: 18px;
  padding-bottom: 12px;
}

.grid_row {
  height: 0;
  > hr {
    margin-right: -999px;
    margin-left: 24px;
    margin-top: 0;
    border-color: $color_even_lighter_gray;
    width: 999px;
  }
}
</style>
